import { ref, onMounted, computed } from '@vue/composition-api'
import store from '@/store'
import axios from '@axios'
import { currency } from '@core/utils/filter'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useDashboardAdmin() {
  // Use toast
  const toast = useToast()

  // Estados reativas
  const isLoading = ref(false)
  const pedidosData = ref([])
  const usuariosData = ref([])
  const dashboardStats = ref({
    totalPedidos: 0,
    totalVendas: 0,
    aguardandoPagamento: 0,
    totalCancelado: 0,
    totalVendedores: 0,
    totalAfiliados: 0,
    totalClientes: 0,
    totalPadrinhos: 0,
    totalProdutos: 0,
    totalCategorias: 0,
  })

  // Função para gerar labels dos últimos 5 meses (incluindo o atual)
  const gerarLabelsUltimos5Meses = () => {
    const nomesMeses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    const dataAtual = new Date()
    const labels = []
    
    for (let i = 4; i >= 0; i -= 1) {
      const data = new Date(dataAtual.getFullYear(), dataAtual.getMonth() - i, 1)
      labels.push(nomesMeses[data.getMonth()])
    }
    
    return labels
  }

  // Dados dos gráficos
  const chartDataDesempenho = ref({
    labels: gerarLabelsUltimos5Meses(),
    datasets: [
      {
        label: 'Vendas',
        data: [0, 0, 0, 0, 0],
        backgroundColor: '#4F7DF3',
      },
      {
        label: 'Receita',
        data: [0, 0, 0, 0, 0],
        backgroundColor: '#28C76F',
      },
    ],
  })

  // Função auxiliar para calcular total de comissões
  const calcularTotalComissoes = pedidoItens => {
    if (!pedidoItens || !Array.isArray(pedidoItens)) return 0
    
    return pedidoItens.reduce((totalPedido, item) => {
      if (!item.comissoes || !Array.isArray(item.comissoes)) return totalPedido
      
      const totalComissoesItem = item.comissoes.reduce((totalItem, comissao) => {
        const valorResto = comissao && comissao.resto ? Number(comissao.resto) : 0
        return totalItem + valorResto
      }, 0)
      
      return totalPedido + totalComissoesItem
    }, 0)
  }

  // Calcular dados do gráfico
  const calculateChartData = () => {
    const pedidos = pedidosData.value
    const labels = gerarLabelsUltimos5Meses()
    const vendasPorMes = new Array(5).fill(0)
    const receitaPorMes = new Array(5).fill(0)
    
    const dataAtual = new Date()
    const mesAtual = dataAtual.getMonth()
    const anoAtual = dataAtual.getFullYear()

    pedidos.forEach(pedido => {
      if (pedido.data_venda) {
        const dataPedido = new Date(pedido.data_venda)
        const mesPedido = dataPedido.getMonth()
        const anoPedido = dataPedido.getFullYear()
        
        // Calcular quantos meses atrás foi o pedido
        const mesesAtras = (anoAtual - anoPedido) * 12 + (mesAtual - mesPedido)
        
        // Se está dentro dos últimos 5 meses (0 a 4 meses atrás)
        if (mesesAtras >= 0 && mesesAtras <= 4) {
          const indice = 4 - mesesAtras // Inverter para que o mais recente fique à direita
          const valorTotal = calcularTotalComissoes(pedido.pedido_itens || [])
          
          if (pedido.status === 'Paid' || pedido.status === 'Completed') {
            vendasPorMes[indice] += 1
            receitaPorMes[indice] += valorTotal
          }
        }
      }
    })

    chartDataDesempenho.value = {
      labels,
      datasets: [
        {
          label: 'Vendas',
          data: vendasPorMes,
          backgroundColor: '#4F7DF3',
        },
        {
          label: 'Receita',
          data: receitaPorMes,
          backgroundColor: '#28C76F',
        },
      ],
    }
  }

  // Calcular estatísticas dos pedidos
  const calculatePedidosStats = () => {
    console.log('Calculando estatísticas dos pedidos...', pedidosData.value)
    const pedidos = pedidosData.value
    
    dashboardStats.value.totalPedidos = pedidos.length

    let totalVendas = 0
    let aguardandoPagamento = 0
    let totalCancelado = 0

    pedidos.forEach(pedido => {
      const valorTotal = calcularTotalComissoes(pedido.pedido_itens || [])
      console.log('Pedido:', pedido.pedido_itens)
      if (pedido.status === 'Paid' || pedido.status === 'Completed') {
        totalVendas += valorTotal
      } else if (pedido.status === 'Pending') {
        aguardandoPagamento += valorTotal
      } else if (pedido.status === 'Canceled') {
        totalCancelado += valorTotal
      }
    })

    dashboardStats.value.totalVendas = totalVendas
    dashboardStats.value.aguardandoPagamento = aguardandoPagamento
    dashboardStats.value.totalCancelado = totalCancelado

    calculateChartData()
  }

  // Calcular estatísticas dos usuários
  const calculateUsuariosStats = () => {
    const usuarios = usuariosData.value
    
    let vendedores = 0
    let afiliados = 0
    let clientes = 0
    let padrinhos = 0

    usuarios.forEach(usuario => {
      const grupoId = usuario.grupo?.id

      console.log('Grupo do usuário:', grupoId)
      
      if (grupoId === 5) {
        vendedores += 1
      } else if (grupoId === 4) {
        afiliados += 1
      } else if (grupoId === 2) {
        clientes += 1
      } else if (grupoId === 6) {
        padrinhos += 1
      }
    })

    dashboardStats.value.totalVendedores = vendedores
    dashboardStats.value.totalAfiliados = afiliados
    dashboardStats.value.totalClientes = clientes
    dashboardStats.value.totalPadrinhos = padrinhos
  }

  // Função para buscar pedidos
  const fetchPedidos = async () => {
    try {
      isLoading.value = true
      const response = await store.dispatch('appClientPedidos/findAllAdmin', {
        perPage: 1000,
        page: 1,
      })
      
      pedidosData.value = response.data.data || []
      calculatePedidosStats()
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error)
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro ao carregar pedidos',
          text: 'Não foi possível carregar os dados dos pedidos',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    } finally {
      isLoading.value = false
    }
  }

  // Função para buscar usuários
  const fetchUsuarios = async () => {
    try {
      const response = await store.dispatch('app-adm-usuario/fetchUsers', {
        perPage: 1000,
        page: 1,
      })
      
      usuariosData.value = response.data.users || []
      calculateUsuariosStats()
    } catch (error) {
      console.error('Erro ao buscar usuários:', error)
      try {
        const response = await axios.get('/users', {
          params: { perPage: 1000, page: 1 },
        })
        usuariosData.value = response.data.users || []
        calculateUsuariosStats()
      } catch (axiosError) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar usuários',
            text: 'Não foi possível carregar os dados dos usuários',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }
  }

  // Função para buscar contagem de produtos
  const fetchProdutos = async () => {
    try {
      const response = await store.dispatch('appAdmProdutos/countAll', {})
      dashboardStats.value.totalProdutos = response.data.count || 0
    } catch (error) {
      console.error('Erro ao buscar contagem de produtos:', error)
      // Em caso de erro, mantém o valor padrão 0
      dashboardStats.value.totalProdutos = 0
    }
  }

  // Função para buscar contagem de categorias
  const fetchCategorias = async () => {
    try {
      const response = await store.dispatch('appAdmCategory/countCategories', {})
      dashboardStats.value.totalCategorias = response.data.count || 0
    } catch (error) {
      console.error('Erro ao buscar contagem de categorias:', error)
      // Em caso de erro, mantém o valor padrão 0
      dashboardStats.value.totalCategorias = 0
    }
  }

  // Dados computados para o template
  const resumoGeral = computed(() => [
    {
      title: 'Total de Pedidos',
      value: dashboardStats.value.totalPedidos.toLocaleString(),
      icon: 'FileTextIcon',
      color: 'primary',
    },
    {
      title: 'Total Vendas',
      value: currency(dashboardStats.value.totalVendas),
      icon: 'DollarSignIcon',
      color: 'success',
    },
    {
      title: 'Aguardando Pagamento',
      value: currency(dashboardStats.value.aguardandoPagamento),
      icon: 'ClockIcon',
      color: 'warning',
    },
    {
      title: 'Total Cancelado',
      value: currency(dashboardStats.value.totalCancelado),
      icon: 'XCircleIcon',
      color: 'danger',
    },
  ])

  const estatisticasAdmin = computed(() => [
    {
      title: 'Categorias',
      value: dashboardStats.value.totalCategorias.toString(),
      icon: 'TagIcon',
      color: 'primary',
    },
    {
      title: 'Produtos',
      value: dashboardStats.value.totalProdutos.toString(),
      icon: 'PackageIcon',
      color: 'warning',
    },
    {
      title: 'Clientes',
      value: dashboardStats.value.totalClientes.toString(),
      icon: 'UsersIcon',
      color: 'info',
    },
    {
      title: 'Vendas',
      value: currency(dashboardStats.value.totalVendas),
      icon: 'DollarSignIcon',
      color: 'success',
    },
  ])

  const contagemUsuarios = computed(() => [
    {
      title: 'Vendedores',
      value: dashboardStats.value.totalVendedores.toString(),
      icon: 'UserIcon',
      color: 'primary',
    },
    {
      title: 'Afiliados',
      value: dashboardStats.value.totalAfiliados.toString(),
      icon: 'UsersIcon',
      color: 'success',
    },
    {
      title: 'Clientes',
      value: dashboardStats.value.totalClientes.toString(),
      icon: 'UserCheckIcon',
      color: 'info',
    },
    {
      title: 'Padrinhos',
      value: dashboardStats.value.totalPadrinhos.toString(),
      icon: 'HeartIcon',
      color: 'warning',
    },
  ])

  // Carregar dados ao montar
  onMounted(() => {
    fetchPedidos()
    fetchUsuarios()
    fetchProdutos()
    fetchCategorias()
  })

  // Função para recarregar dados
  const refetchData = async () => {
    await Promise.all([
      fetchPedidos(),
      fetchUsuarios(),
      fetchProdutos(),
      fetchCategorias(),
    ])
  }

  return {
    // Estados
    isLoading,
    
    // Dados computados
    resumoGeral,
    estatisticasAdmin,
    contagemUsuarios,
    chartDataDesempenho,
    
    // Métodos
    refetchData,
    fetchPedidos,
    fetchUsuarios,
    fetchProdutos,
    fetchCategorias,
  }
}
